import { format, formatDistanceToNow } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PageBackBar from "../components/PageBackBar";
import { RoleLabel } from "../features/auth/types";
import { useProvisionJob, useProvisionJobLogs, useProvisionJobs } from "../features/jobs/hooks";
import {
  extractWorkvivoUserIdFromLogMessage,
  parseProvisionLogMessage
} from "../features/jobs/parseProvisionLogMessage";
import type { ParsedProvisionLogMessage } from "../features/jobs/parseProvisionLogMessage";
import { downloadProvisionLogsCsv } from "../features/jobs/provisionLogsCsv";
import type { ProvisionJobLog, ProvisionJobStatus, ProvisionJobType } from "../features/jobs/types";
import "../App.css";

const PAGE_SIZE = 10;

/** Lower sort rank = appears first (failures pinned to top). */
const logStatusSortRank = (status: ProvisionJobStatus): number => {
  switch (status) {
    case "failed":
      return 0;
    case "completed":
      return 1;
    default:
      return 2;
  }
};

type LogFilter = "all" | "failed" | "completed";

function slugForFilename(name: string): string {
  const s = name
    .replace(/[^\w\s-]/g, "")
    .trim()
    .slice(0, 48)
    .replace(/\s+/g, "-")
    .toLowerCase();
  return s || "provision-job";
}

function formatJobTypePhrase(t: ProvisionJobType): string {
  switch (t) {
    case "create_user":
      return "Create users";
    case "update_user":
      return "Update users";
    case "delete_user":
      return "Delete users";
  }
}

function formatLogActionRowLabel(t: ProvisionJobType): string {
  switch (t) {
    case "create_user":
      return "Create user";
    case "update_user":
      return "Update user";
    case "delete_user":
      return "Delete user";
  }
}

function actionIcon(logType: ProvisionJobType): string {
  switch (logType) {
    case "create_user":
      return "＋";
    case "update_user":
      return "↻";
    case "delete_user":
      return "×";
  }
}

function jobStatusPresentation(status: ProvisionJobStatus): { label: string; bg: string; color: string } {
  switch (status) {
    case "completed":
      return { label: "Completed", bg: "#dcfce7", color: "#15803d" };
    case "in_progress":
      return { label: "In progress", bg: "#dbeafe", color: "#1d4ed8" };
    case "failed":
      return { label: "Failed", bg: "#fee2e2", color: "#b91c1c" };
    default:
      return { label: "Pending", bg: "#fef3c7", color: "#b45309" };
  }
}

function logStatusPresentation(status: ProvisionJobStatus): { label: string; bg: string; color: string; icon: string } {
  switch (status) {
    case "failed":
      return { label: "Failed", bg: "#fef2f2", color: "#b91c1c", icon: "✕" };
    case "completed":
      return { label: "Completed", bg: "#ecfdf5", color: "#047857", icon: "✓" };
    default: {
      const label = status === "in_progress" ? "In progress" : "Pending";
      return {
        label,
        bg: "#fef3c7",
        color: "#b45309",
        icon: "◷"
      };
    }
  }
}

function failureSnippet(parsed: ParsedProvisionLogMessage, raw: string): string {
  const line = parsed.details[0] ?? parsed.rawFallback ?? raw;
  const trimmed = line.trim();
  return trimmed.length > 140 ? `${trimmed.slice(0, 137)}…` : trimmed;
}

function LogRowRecordCell({ message }: { message?: string | null }) {
  const parsed = parseProvisionLogMessage(message);
  if (parsed.rawFallback !== undefined) {
    return (
      <span style={{ fontSize: "0.82rem", color: "#475569", lineHeight: 1.45, wordBreak: "break-word" }}>
        {parsed.rawFallback}
      </span>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.82rem", lineHeight: 1.4 }}>
      {parsed.spreadsheetRow !== undefined ? (
        <span
          style={{
            display: "inline-flex",
            alignSelf: "flex-start",
            padding: "0.12rem 0.45rem",
            borderRadius: "0.35rem",
            backgroundColor: "#e2e8f0",
            color: "#475569",
            fontWeight: 700,
            fontSize: "0.76rem",
            fontVariantNumeric: "tabular-nums"
          }}
        >
          Row {parsed.spreadsheetRow}
        </span>
      ) : null}
      {parsed.contextFields.map((field, idx) => (
        <div key={`${field.key}-${idx}`} style={{ color: "#334155" }}>
          <span style={{ color: "#94a3b8", fontWeight: 600 }}>{field.key}: </span>
          <span style={{ wordBreak: "break-word" }}>{field.value}</span>
        </div>
      ))}
    </div>
  );
}

function LogSummaryCell({ message }: { message?: string | null }) {
  const parsed = parseProvisionLogMessage(message);
  const wv = extractWorkvivoUserIdFromLogMessage(message);
  const title = parsed.details[0] ?? parsed.rawFallback ?? "—";
  const sub = parsed.details.slice(1).join(" · ");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", maxWidth: "min(420px, 36vw)" }}>
      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.86rem", lineHeight: 1.35 }}>{title}</div>
      {sub ? (
        <div style={{ fontSize: "0.78rem", color: "#64748b", lineHeight: 1.4, wordBreak: "break-word" }}>{sub}</div>
      ) : null}
      {wv ? (
        <span
          style={{
            display: "inline-flex",
            alignSelf: "flex-start",
            padding: "0.15rem 0.45rem",
            borderRadius: "0.35rem",
            backgroundColor: "#f1f5f9",
            color: "#475569",
            fontSize: "0.72rem",
            fontWeight: 600,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
          }}
        >
          User ID {wv}
        </span>
      ) : null}
    </div>
  );
}

const JobLogsPage = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const jobsQuery = useProvisionJobs();
  const logsQuery = useProvisionJobLogs(jobId ?? null, true, { refetchOnMount: "always" });

  useEffect(() => {
    void jobsQuery.refetch();
  }, [jobId]);
  const [logFilter, setLogFilter] = useState<LogFilter>("all");
  const [messageSearch, setMessageSearch] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedJobId, setCopiedJobId] = useState(false);
  const [page, setPage] = useState(1);

  // Try list cache first; fall back to direct fetch for jobs not in the current list
  // (e.g. a superuser opening another user's job from the "All jobs" view)
  const jobFromList = useMemo(
    () => jobsQuery.data?.find((item) => item.id === jobId),
    [jobsQuery.data, jobId]
  );
  const jobDirectQuery = useProvisionJob(jobFromList ? null : (jobId ?? null));
  const job = jobFromList ?? jobDirectQuery.data;

  const logSummary = useMemo(() => {
    const logs = logsQuery.data ?? [];
    return logs.reduce(
      (acc, log) => {
        acc.total += 1;
        if (log.status === "completed") acc.completed += 1;
        else if (log.status === "failed") acc.failed += 1;
        else acc.other += 1;
        return acc;
      },
      { total: 0, completed: 0, failed: 0, other: 0 }
    );
  }, [logsQuery.data]);

  const sortedLogs = useMemo(() => {
    const logs = [...(logsQuery.data ?? [])];
    return logs.sort((a, b) => {
      const r = logStatusSortRank(a.status) - logStatusSortRank(b.status);
      if (r !== 0) return r;
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [logsQuery.data]);

  const effectiveLogs = useMemo(() => {
    const q = messageSearch.trim().toLowerCase();
    return sortedLogs.filter((log) => {
      if (logFilter === "failed" && log.status !== "failed") return false;
      if (logFilter === "completed" && log.status !== "completed") return false;
      if (!q) return true;
      const msg = (log.message ?? "").toLowerCase();
      if (msg.includes(q)) return true;
      const parsed = parseProvisionLogMessage(log.message);
      const ctx = parsed.contextFields.some((f) => f.value.toLowerCase().includes(q));
      if (ctx) return true;
      if (parsed.spreadsheetRow !== undefined && String(parsed.spreadsheetRow).includes(q)) return true;
      return false;
    });
  }, [sortedLogs, logFilter, messageSearch]);

  const totalPages = Math.max(1, Math.ceil(effectiveLogs.length / PAGE_SIZE));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [logFilter, messageSearch]);

  const pageClamped = Math.min(page, totalPages);
  const sliceStart = (pageClamped - 1) * PAGE_SIZE;
  const pageLogs = effectiveLogs.slice(sliceStart, sliceStart + PAGE_SIZE);

  const lastLogAt = useMemo(() => {
    const logs = logsQuery.data ?? [];
    if (!logs.length) return null;
    return logs.reduce((max, l) => {
      const t = new Date(l.timestamp).getTime();
      return t > max ? t : max;
    }, 0);
  }, [logsQuery.data]);

  const successRowPct =
    logSummary.total > 0 ? Math.round((logSummary.completed / logSummary.total) * 100) : null;
  const failedRowPct = logSummary.total > 0 ? Math.round((logSummary.failed / logSummary.total) * 100) : null;

  const pct = (n: number) => (logSummary.total > 0 ? (n / logSummary.total) * 100 : 0);

  const failedAttention = useMemo(() => {
    const failed = (logsQuery.data ?? []).filter((l) => l.status === "failed");
    return failed
      .map((log) => {
        const parsed = parseProvisionLogMessage(log.message);
        const row = parsed.spreadsheetRow;
        return { log, parsed, row: row ?? 999999 };
      })
      .sort((a, b) => a.row - b.row);
  }, [logsQuery.data]);

  const copyLogMessage = (log: ProvisionJobLog) => {
    const text = log.message ?? "";
    void navigator.clipboard.writeText(text).then(() => {
      setCopiedId(log.id);
      window.setTimeout(() => setCopiedId((id) => (id === log.id ? null : id)), 2000);
    });
  };

  const copyJobId = () => {
    if (!jobId) return;
    void navigator.clipboard.writeText(jobId).then(() => {
      setCopiedJobId(true);
      window.setTimeout(() => setCopiedJobId(false), 2000);
    });
  };

  const exportCsv = () => {
    const raw = logsQuery.data ?? [];
    if (raw.length === 0) return;
    const base = slugForFilename(job?.name ?? "provision-job");
    const idPart = jobId?.slice(0, 8) ?? "logs";
    downloadProvisionLogsCsv(raw, `${base}-${idPart}`);
  };

  const scrollToRowActivity = () => {
    document.getElementById("row-activity")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setLogFilter("failed");
    setPage(1);
  };

  const rangeLabel =
    effectiveLogs.length === 0
      ? "0 rows"
      : `Showing ${sliceStart + 1}-${Math.min(sliceStart + PAGE_SIZE, effectiveLogs.length)} of ${effectiveLogs.length} row${
          effectiveLogs.length === 1 ? "" : "s"
        }`;

  const jobStatus = job ? jobStatusPresentation(job.status) : null;

  return (
    <div className="job-logs-page">
      <div>
      <PageBackBar />
        <h1 className="job-logs-page-title" style={{ marginTop: "0.85rem" }}>
          Provisioning log
        </h1>
        <p className="job-logs-page-sub">
          Review upload details, row outcomes, and any issues that need attention.
        </p>
      </div>

      {job || jobId ? (
        <div className="job-logs-overview-row">
          <div className="job-logs-overview-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a" }}>
                  {job ? format(new Date(job.createdAt), "MM.dd.yyyy") : "—"}
                </div>
                <div style={{ marginTop: "0.25rem", fontSize: "1rem", fontWeight: 700, color: "#334155" }}>
                  {job?.name ?? "Provisioning job"}
                </div>
                <div style={{ marginTop: "0.2rem", fontSize: "0.82rem", color: "#64748b" }}>
                  {job ? `${formatJobTypePhrase(job.jobType)} upload` : "Job details"}
                  {job ? (
                    <>
                      {" "}
                      · {job.createdBy.name} ({RoleLabel[job.createdBy.role] ?? job.createdBy.role})
                    </>
                  ) : null}
                </div>
              </div>
              {jobStatus ? (
                <span
                  style={{
                    padding: "0.3rem 0.75rem",
                    borderRadius: "999px",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    backgroundColor: jobStatus.bg,
                    color: jobStatus.color,
                    whiteSpace: "nowrap"
                  }}
                >
                  {jobStatus.label}
                </span>
              ) : (
                <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>Status loading…</span>
              )}
            </div>

            <div style={{ marginTop: "1.1rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8", fontWeight: 800 }}>
                Job ID
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                <code style={{ fontSize: "0.78rem", wordBreak: "break-all", color: "#0f172a", fontWeight: 600 }}>
                  {job?.id ?? jobId}
                </code>
                <button
                  type="button"
                  className="dashboard-btn-outline"
                  style={{ padding: "0.25rem 0.55rem", fontSize: "0.75rem" }}
                  onClick={() => copyJobId()}
                  title="Copy job ID"
                >
                  {copiedJobId ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            {job ? (
              <div
                style={{
                  marginTop: "1rem",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "0.85rem 1rem"
                }}
              >
                <div>
                  <div style={{ fontSize: "0.7rem", textTransform: "uppercase", color: "#94a3b8", fontWeight: 800 }}>Created</div>
                  <div style={{ marginTop: "0.25rem", fontSize: "0.86rem", color: "#334155", fontWeight: 500 }}>
                    {format(new Date(job.createdAt), "MMM d, yyyy h:mm a")}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.7rem", textTransform: "uppercase", color: "#94a3b8", fontWeight: 800 }}>Created by</div>
                  <div style={{ marginTop: "0.25rem", fontWeight: 600, color: "#334155" }}>{job.createdBy.name}</div>
                  <div style={{ fontSize: "0.8rem", color: "#64748b" }}>{job.createdBy.email}</div>
                </div>
                {job.description?.trim() || job.details?.trim() ? (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: "0.7rem", textTransform: "uppercase", color: "#94a3b8", fontWeight: 800 }}>
                      Notes / description
                    </div>
                    <div style={{ marginTop: "0.25rem", fontSize: "0.86rem", color: "#475569", lineHeight: 1.45 }}>
                      {job.description?.trim() || job.details}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {logSummary.total > 0 ? (
              <div style={{ marginTop: "1.15rem", paddingTop: "1rem", borderTop: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8", fontWeight: 800, marginBottom: "0.45rem" }}>
                  Row results
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center" }}>
                  <span
                    style={{
                      padding: "0.25rem 0.55rem",
                      borderRadius: "0.45rem",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      backgroundColor: "#f1f5f9",
                      color: "#475569",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    {logSummary.total} total
                  </span>
                  <span
                    style={{
                      padding: "0.25rem 0.55rem",
                      borderRadius: "0.45rem",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      backgroundColor: "#ecfdf5",
                      color: "#047857",
                      border: "1px solid #a7f3d0"
                    }}
                  >
                    {logSummary.completed} completed
                  </span>
                  <span
                    style={{
                      padding: "0.25rem 0.55rem",
                      borderRadius: "0.45rem",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      backgroundColor: "#fef2f2",
                      color: "#b91c1c",
                      border: "1px solid #fecaca"
                    }}
                  >
                    {logSummary.failed} failed
                  </span>
                </div>
                <div className="job-card-progress-track" style={{ marginTop: "0.55rem" }}>
                  {logSummary.completed > 0 ? (
                    <div className="job-card-progress-seg" style={{ width: `${pct(logSummary.completed)}%`, background: "#22c55e" }} />
                  ) : null}
                  {logSummary.failed > 0 ? (
                    <div className="job-card-progress-seg" style={{ width: `${pct(logSummary.failed)}%`, background: "#f87171" }} />
                  ) : null}
                  {logSummary.other > 0 ? (
                    <div className="job-card-progress-seg" style={{ width: `${pct(logSummary.other)}%`, background: "#cbd5e1" }} />
                  ) : null}
                </div>
                {successRowPct !== null ? (
                  <div style={{ marginTop: "0.35rem", fontSize: "0.8rem", fontWeight: 600, color: "#334155" }}>{successRowPct}% successful</div>
                ) : null}
              </div>
            ) : null}
          </div>

          {failedAttention.length > 0 ? (
            <div className="job-logs-attention-card">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
                  <span style={{ fontSize: "1.15rem" }} aria-hidden>
                    ⚠
                  </span>
                  <span style={{ fontSize: "1rem", fontWeight: 800, color: "#991b1b" }}>Needs attention</span>
                </div>
                <span
                  style={{
                    padding: "0.2rem 0.55rem",
                    borderRadius: "999px",
                    fontSize: "0.78rem",
                    fontWeight: 800,
                    backgroundColor: "#fee2e2",
                    color: "#b91c1c",
                    border: "1px solid #fecaca"
                  }}
                >
                  {failedAttention.length}
                </span>
              </div>
              <p style={{ margin: "0.65rem 0 0", fontSize: "0.88rem", color: "#7f1d1d", lineHeight: 1.45 }}>
                {failedAttention.length} row{failedAttention.length === 1 ? "" : "s"} failed due to validation or catalog issues.
              </p>
              <ul style={{ margin: "0.75rem 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {failedAttention.slice(0, 4).map(({ log, parsed }) => (
                  <li
                    key={log.id}
                    style={{
                      padding: "0.55rem 0.65rem",
                      borderRadius: "0.55rem",
                      backgroundColor: "#fff",
                      border: "1px solid #fecaca",
                      fontSize: "0.8rem",
                      color: "#7f1d1d",
                      lineHeight: 1.4
                    }}
                  >
                    {parsed.spreadsheetRow !== undefined ? (
                      <strong style={{ fontWeight: 800 }}>Row {parsed.spreadsheetRow}: </strong>
                    ) : null}
                    {failureSnippet(parsed, log.message ?? "")}
                  </li>
                ))}
              </ul>
              {failedAttention.length > 4 ? (
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
                  +{failedAttention.length - 4} more in the table below.
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => scrollToRowActivity()}
                style={{
                  marginTop: "0.85rem",
                  padding: 0,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  color: "#b91c1c",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px"
                }}
              >
                View failed rows →
              </button>
            </div>
          ) : (
            <div
              className="job-logs-overview-card"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: "8rem",
                borderStyle: "dashed",
                background: "#fafafa"
              }}
            >
              <div style={{ fontWeight: 700, color: "#047857", fontSize: "0.95rem" }}>All clear</div>
              <p style={{ margin: "0.35rem 0 0", fontSize: "0.86rem", color: "#64748b", lineHeight: 1.45 }}>
                No failed rows in this batch. Row-level failures would appear here with a short reason.
              </p>
            </div>
          )}
        </div>
      ) : null}

      {logSummary.total > 0 ? (
        <section className="dashboard-kpi-row" aria-label="Row statistics">
          <div className="dashboard-kpi-card">
            <div className="dashboard-kpi-icon" style={{ background: "#eff6ff", color: "#2563eb" }} aria-hidden>
              📄
            </div>
            <div style={{ minWidth: 0 }}>
              <p className="dashboard-kpi-label">Total rows</p>
              <p className="dashboard-kpi-value">{logSummary.total}</p>
              <p className="dashboard-kpi-sub">All rows processed</p>
            </div>
          </div>
          <div className="dashboard-kpi-card">
            <div className="dashboard-kpi-icon" style={{ background: "#ecfdf5", color: "#16a34a" }} aria-hidden>
              ✓
            </div>
            <div style={{ minWidth: 0 }}>
              <p className="dashboard-kpi-label">Completed</p>
              <p className="dashboard-kpi-value">{logSummary.completed}</p>
              <p className="dashboard-kpi-sub">{successRowPct !== null ? `${successRowPct}% of rows` : "—"}</p>
            </div>
          </div>
          <div className="dashboard-kpi-card">
            <div className="dashboard-kpi-icon" style={{ background: "#fef2f2", color: "#ef4444" }} aria-hidden>
              ✕
            </div>
            <div style={{ minWidth: 0 }}>
              <p className="dashboard-kpi-label">Failed</p>
              <p className="dashboard-kpi-value">{logSummary.failed}</p>
              <p className="dashboard-kpi-sub">{failedRowPct !== null ? `${failedRowPct}% of rows` : "—"}</p>
            </div>
          </div>
          <div className="dashboard-kpi-card">
            <div className="dashboard-kpi-icon" style={{ background: "#f5f3ff", color: "#7c3aed" }} aria-hidden>
              🕐
            </div>
            <div style={{ minWidth: 0 }}>
              <p className="dashboard-kpi-label">Last updated</p>
              <p className="dashboard-kpi-value" style={{ fontSize: "1.05rem", fontWeight: 700 }}>
                {lastLogAt
                  ? formatDistanceToNow(new Date(lastLogAt), { addSuffix: true })
                  : "—"}
              </p>
              <p className="dashboard-kpi-sub">
                {lastLogAt ? format(new Date(lastLogAt), "MMM d, yyyy 'at' h:mm a") : "No log timestamps yet"}
              </p>
            </div>
          </div>
        </section>
      ) : null}

      {logsQuery.isLoading ? (
        <div style={{ color: "#94a3b8" }}>Loading logs…</div>
      ) : logsQuery.isError ? (
        <div style={{ color: "#b91c1c" }}>Failed to load logs for this job.</div>
      ) : (logsQuery.data?.length ?? 0) === 0 ? (
        <div style={{ color: "#64748b", padding: "1.5rem", border: "1px dashed #cbd5e1", borderRadius: "1rem", background: "#f8fafc" }}>
          No log entries recorded yet.
        </div>
      ) : (
        <div id="row-activity" className="job-logs-table-wrap" style={{ scrollMarginTop: "1rem" }}>
          <div className="job-logs-row-activity-head">
            <h2>Row activity</h2>
            <p>
              Filters and search only change this view. <strong style={{ fontWeight: 650, color: "#334155" }}>Export CSV</strong> still
              includes every row for this job.
            </p>
          </div>

          <div className="job-logs-tab-row" role="tablist" aria-label="Row status filters">
            <button
              type="button"
              role="tab"
              aria-selected={logFilter === "all"}
              className="job-logs-tab"
              data-active={logFilter === "all"}
              onClick={() => setLogFilter("all")}
            >
              All ({logSummary.total})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={logFilter === "failed"}
              className="job-logs-tab"
              data-active={logFilter === "failed"}
              onClick={() => setLogFilter("failed")}
            >
              Failed ({logSummary.failed})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={logFilter === "completed"}
              className="job-logs-tab"
              data-active={logFilter === "completed"}
              onClick={() => setLogFilter("completed")}
            >
              Completed ({logSummary.completed})
            </button>
            <input
              type="search"
              className="job-list-search"
              style={{ marginLeft: "auto", maxWidth: "280px", flex: "1 1 180px" }}
              value={messageSearch}
              onChange={(e) => setMessageSearch(e.target.value)}
              placeholder="Search rows…"
              aria-label="Search rows"
            />
            <button type="button" className="dashboard-btn-outline" onClick={() => exportCsv()} disabled={!logsQuery.data?.length}>
              ⬇ Export CSV
            </button>
          </div>

          <div className="job-logs-table-scroll">
            <table className="job-logs-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Action</th>
                  <th style={{ minWidth: "200px" }}>Row / record</th>
                  <th style={{ minWidth: "220px" }}>Summary</th>
                  <th>Timestamp</th>
                  <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {effectiveLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: "1.5rem 0.85rem", color: "#64748b", fontSize: "0.88rem" }}>
                      No rows match your filters.{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setLogFilter("all");
                          setMessageSearch("");
                        }}
                        style={{
                          marginLeft: "0.35rem",
                          border: "none",
                          background: "none",
                          color: "#2563eb",
                          fontWeight: 650,
                          cursor: "pointer",
                          textDecoration: "underline",
                          padding: 0,
                          font: "inherit"
                        }}
                      >
                        Reset filters
                      </button>
                    </td>
                  </tr>
                ) : null}
                {pageLogs.map((log) => {
                  const st = logStatusPresentation(log.status);
                  const isFailed = log.status === "failed";
                  const rowBg = isFailed ? "#fff5f5" : log.status === "completed" ? "#fff" : "#fffbeb";

                  return (
                    <tr
                      key={log.id}
                      style={{
                        backgroundColor: rowBg,
                        borderLeft: isFailed ? "3px solid #fecaca" : undefined
                      }}
                    >
                      <td>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            padding: "0.18rem 0.5rem",
                            borderRadius: "999px",
                            fontSize: "0.74rem",
                            fontWeight: 700,
                            backgroundColor: st.bg,
                            color: st.color,
                            border: `1px solid ${isFailed ? "#fecaca" : st.bg}`
                          }}
                        >
                          <span aria-hidden>{st.icon}</span>
                          {st.label}
                        </span>
                      </td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.82rem", color: "#334155", fontWeight: 600 }}>
                          <span style={{ color: "#64748b" }} aria-hidden>
                            {actionIcon(log.logType)}
                          </span>
                          {formatLogActionRowLabel(log.logType)}
                        </span>
                      </td>
                      <td>
                        <LogRowRecordCell message={log.message} />
                      </td>
                      <td>
                        <LogSummaryCell message={log.message} />
                      </td>
                      <td style={{ color: "#64748b", fontSize: "0.78rem", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                        {format(new Date(log.timestamp), "MMM d, yyyy h:mm a")}
                      </td>
                      <td style={{ textAlign: "right", verticalAlign: "top" }}>
                        <button
                          type="button"
                          onClick={() => copyLogMessage(log)}
                          title="Copy full log line"
                          className="dashboard-btn-outline"
                          style={{
                            padding: "0.28rem 0.55rem",
                            fontSize: "0.75rem",
                            color: copiedId === log.id ? "#047857" : undefined,
                            borderColor: "#e2e8f0"
                          }}
                        >
                          {copiedId === log.id ? "Copied" : "Copy details →"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {effectiveLogs.length > 0 ? (
            <nav className="job-logs-pagination" aria-label="Row pagination">
              <span>{rangeLabel}</span>
              <div className="job-logs-page-btns">
                <button type="button" onClick={() => setPage(1)} disabled={pageClamped <= 1} aria-label="First page">
                  «
                </button>
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageClamped <= 1} aria-label="Previous page">
                  ‹
                </button>
                {(() => {
                  if (totalPages <= 7) {
                    return Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                      <button key={n} type="button" data-active={n === pageClamped} onClick={() => setPage(n)}>
                        {n}
                      </button>
                    ));
                  }
                  const set = new Set([
                    1,
                    2,
                    totalPages - 1,
                    totalPages,
                    pageClamped,
                    pageClamped - 1,
                    pageClamped + 1
                  ]);
                  const nums = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
                  const out: (number | "ellipsis")[] = [];
                  for (let i = 0; i < nums.length; i++) {
                    if (i > 0 && nums[i] - nums[i - 1] > 1) out.push("ellipsis");
                    out.push(nums[i]);
                  }
                  return out.map((item, idx) =>
                    item === "ellipsis" ? (
                      <span key={`e-${idx}`} style={{ padding: "0 0.1rem", color: "#94a3b8", fontWeight: 600 }} aria-hidden>
                        …
                      </span>
                    ) : (
                      <button key={item} type="button" data-active={item === pageClamped} onClick={() => setPage(item)}>
                        {item}
                      </button>
                    )
                  );
                })()}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={pageClamped >= totalPages}
                  aria-label="Next page"
                >
                  ›
                </button>
                <button type="button" onClick={() => setPage(totalPages)} disabled={pageClamped >= totalPages} aria-label="Last page">
                  »
                </button>
              </div>
            </nav>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default JobLogsPage;
